import { clsx } from "clsx";
import { animate, motion, useMotionValue } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";

import { useIsMobile } from "@/hooks/use-mobile";

const SEGMENT_GAP = 1;
const SNAP_SPRING = { type: "spring", stiffness: 500, damping: 35 } as const;

const stepToPixel = (
  value: number,
  min: number,
  max: number,
  trackWidth: number,
) => ((value - min) / (max - min)) * trackWidth;

const pixelToNearestStep = (
  px: number,
  min: number,
  max: number,
  step: number,
  trackWidth: number,
) => {
  const clamped = Math.max(0, Math.min(trackWidth, px));
  const raw = (clamped / trackWidth) * (max - min) + min;
  const nearest = Math.round((raw - min) / step) * step + min;
  return Math.max(min, Math.min(max, nearest));
};

interface SliderProps {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  width?: number;
  variant?: "segmented" | "continuous";
}

export const Slider = ({
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.1,
  width,
  variant = "segmented",
}: SliderProps) => {
  const isMobile = useIsMobile();
  const maxWidth = width ?? (isMobile ? 200 : 270);

  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState<number>(maxWidth);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setTrackWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const segmentCount = Math.round((max - min) / step);
  const steps = Array.from(
    { length: segmentCount + 1 },
    (_, i) => min + i * step,
  );
  const gap = variant === "continuous" ? 0 : SEGMENT_GAP;
  const segmentWidth = (trackWidth - gap * (segmentCount - 1)) / segmentCount;

  const thumbX = useMotionValue(stepToPixel(value, min, max, trackWidth));

  useEffect(() => {
    animate(thumbX, stepToPixel(value, min, max, trackWidth), {
      type: "spring",
      stiffness: 1400,
      damping: 55,
    });
  }, [value, thumbX, min, max, trackWidth]);

  const snapThumbToNearest = useCallback(
    (px: number) => {
      const nearest = pixelToNearestStep(px, min, max, step, trackWidth);
      animate(thumbX, stepToPixel(nearest, min, max, trackWidth), SNAP_SPRING);
      onChange(nearest);
    },
    [thumbX, onChange, min, max, step, trackWidth],
  );

  return (
    <div
      ref={trackRef}
      role="presentation"
      className="relative cursor-pointer w-full min-w-0 flex-1"
      style={{ maxWidth: `${maxWidth}px`, height: "20px" }}
      onClick={(e) => {
        if (!trackRef.current) return;
        const rect = trackRef.current.getBoundingClientRect();
        snapThumbToNearest(e.clientX - rect.left);
      }}
    >
      {/* Track */}
      <div
        className={clsx(
          "absolute flex w-full",
          variant === "continuous" && "rounded-full overflow-hidden",
        )}
        style={{
          height: "8px",
          top: "50%",
          transform: "translateY(-50%)",
          gap: `${gap}px`,
        }}
      >
        {Array.from({ length: segmentCount }, (_, i) => {
          const isFilled = value >= steps[i + 1] - step * 0.01;
          const isFirst = i === 0;
          const isLast = i === segmentCount - 1;
          return (
            <div
              key={i}
              style={{
                width: `${segmentWidth}px`,
                flexShrink: 0,
                backgroundColor: isFilled ? "#2563eb" : "#e5e7eb",
                borderRadius:
                  variant === "continuous"
                    ? undefined
                    : isFirst
                      ? "4px 0 0 4px"
                      : isLast
                        ? "0 4px 4px 0"
                        : "0",
              }}
            />
          );
        })}
      </div>

      {/* Thumb */}
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: trackWidth }}
        dragElastic={0}
        dragMomentum={false}
        style={{
          x: thumbX,
          position: "absolute",
          top: "50%",
          left: 0,
          marginTop: "-10px",
          marginLeft: "-10px",
        }}
        onDragEnd={() => snapThumbToNearest(thumbX.get())}
        className="w-5 h-5 rounded-full bg-white border border-gray-300 shadow-md cursor-grab active:cursor-grabbing"
      />
    </div>
  );
};
